/**
 * Morgan extension: business-document file icons for the Finder.
 *
 * The @pierre/trees "complete" sprite set is a code-file icon suite — it has
 * no glyphs for the document types Morgan actually stores. Every PDF, Word
 * document, email and Outlook archive resolves to the gray "default" token,
 * which reads as "icons are missing". This module supplies sprite symbols in
 * the same visual language as the built-in set (16×16 filled paths, folded
 * document corner, 40% silhouette) plus a `byFileExtension` remap consumed
 * by both the icon resolver (icon/extended-list/column/gallery views) and
 * the tree's shadow-DOM icon config, so every view shares one code path.
 *
 * Coloring: the built-in set colors glyphs through `data-icon-token` CSS
 * that only exists for built-in tokens, so these symbols self-color via the
 * `--fs-file-icon-*` custom properties declared on :root (custom properties
 * inherit through shadow boundaries, so the tree picks them up too). The
 * token colors live in `MORGAN_FILE_ICON_COLORS`, merged into the Finder's
 * FILE_ICON_COLORS map so they participate in the existing dark-mode /
 * on-light / on-primary palette flips.
 */

import type { RemappedIcon } from "@pierre/trees";

/** `fill` value for a Morgan icon token: themed var, currentColor fallback. */
function tokenFill(token: string) {
  return `var(--fs-file-icon-${token}, currentColor)`;
}

// Shared geometry lifted from the built-in set so the custom glyphs sit
// pixel-identical next to it: document silhouette + folded top-right corner.
const DOC_BODY =
  "M8 4a3 3 0 0 0 3 3h3v5.5a2.5 2.5 0 0 1-2.5 2.5h-7A2.5 2.5 0 0 1 2 12.5v-9A2.5 2.5 0 0 1 4.5 1H8z";
const DOC_CORNER =
  "M9.5 1a.5.5 0 0 1 .354.146l4 4A.5.5 0 0 1 14 5.5V6h-3a2 2 0 0 1-2-2V1z";

function documentSymbol(id: string, token: string, detail: string) {
  return (
    `<symbol id="${id}" viewBox="0 0 16 16">` +
    `<path fill="${tokenFill(token)}" fill-rule="evenodd" clip-rule="evenodd" d="${DOC_BODY}" opacity=".4"/>` +
    `<path fill="${tokenFill(token)}" d="${DOC_CORNER} ${detail}"/>` +
    `</symbol>`
  );
}

// Two text lines — the built-in "text" detail, recolored per type.
const TEXT_LINES =
  "M8.5 11a.5.5 0 0 1 0 1h-3a.5.5 0 0 1 0-1zm2-2a.5.5 0 0 1 0 1h-5a.5.5 0 0 1 0-1z";
// Three paragraph lines for word-processing documents.
const PARAGRAPH_LINES =
  "M8.5 11a.5.5 0 0 1 0 1h-3a.5.5 0 0 1 0-1zm2-2a.5.5 0 0 1 0 1h-5a.5.5 0 0 1 0-1zm0-2a.5.5 0 0 1 0 1h-5a.5.5 0 0 1 0-1z";
// Spreadsheet grid — the built-in "table" detail.
const TABLE_GRID =
  "M11.5 8a.5.5 0 0 1 .5.5v4a.5.5 0 0 1-.5.5h-7a.5.5 0 0 1-.5-.5v-4a.5.5 0 0 1 .5-.5zM5 12h2.5v-1H5zm3.5 0H11v-1H8.5zM5 10h2.5V9H5zm3.5 0H11V9H8.5z";
// A slide frame for presentations.
const SLIDE_FRAME =
  "M10.5 8.5a.5.5 0 0 1 .5.5v3a.5.5 0 0 1-.5.5h-5a.5.5 0 0 1-.5-.5V9a.5.5 0 0 1 .5-.5zM6 11.5h4v-2H6z";

function envelopeSymbol(id: string, token: string) {
  return (
    `<symbol id="${id}" viewBox="0 0 16 16">` +
    `<path fill="${tokenFill(token)}" d="M3.5 3h9A2.5 2.5 0 0 1 15 5.5v5a2.5 2.5 0 0 1-2.5 2.5h-9A2.5 2.5 0 0 1 1 10.5v-5A2.5 2.5 0 0 1 3.5 3z" opacity=".4"/>` +
    `<path fill="${tokenFill(token)}" d="M1.2 4.49a2.5 2.5 0 0 1 .83-1.06L8 7.65l5.97-4.22a2.5 2.5 0 0 1 .83 1.06L8.58 8.92a1 1 0 0 1-1.16 0z"/>` +
    `</symbol>`
  );
}

/**
 * Symbol definitions appended to both the light-DOM sprite mount and the
 * tree's shadow-DOM sprite sheet.
 */
export const MORGAN_FILE_ICON_SYMBOLS = [
  documentSymbol("morgan-icon-pdf", "pdf", TEXT_LINES),
  documentSymbol("morgan-icon-word", "word", PARAGRAPH_LINES),
  documentSymbol("morgan-icon-excel", "excel", TABLE_GRID),
  documentSymbol("morgan-icon-powerpoint", "powerpoint", SLIDE_FRAME),
  envelopeSymbol("morgan-icon-email", "email"),
].join("");

/** Standalone sprite block for the light-DOM mount. */
export const MORGAN_FILE_ICON_SPRITE_SHEET = `<svg aria-hidden="true" width="0" height="0">${MORGAN_FILE_ICON_SYMBOLS}</svg>`;

const ICON_PDF: RemappedIcon = {
  name: "morgan-icon-pdf",
  viewBox: "0 0 16 16",
};
const ICON_WORD: RemappedIcon = {
  name: "morgan-icon-word",
  viewBox: "0 0 16 16",
};
const ICON_EXCEL: RemappedIcon = {
  name: "morgan-icon-excel",
  viewBox: "0 0 16 16",
};
const ICON_POWERPOINT: RemappedIcon = {
  name: "morgan-icon-powerpoint",
  viewBox: "0 0 16 16",
};
const ICON_EMAIL: RemappedIcon = {
  name: "morgan-icon-email",
  viewBox: "0 0 16 16",
};

/**
 * Extension remap shared by the resolver behind every light-DOM view and the
 * tree's icon config. Extensions not listed here (csv, txt, images, zip…)
 * keep their built-in glyphs, which are already distinct and colored.
 */
export const MORGAN_FILE_ICON_EXTENSIONS: Record<string, RemappedIcon> = {
  doc: ICON_WORD,
  docx: ICON_WORD,
  dot: ICON_WORD,
  dotx: ICON_WORD,
  eml: ICON_EMAIL,
  msg: ICON_EMAIL,
  odt: ICON_WORD,
  ods: ICON_EXCEL,
  ost: ICON_EMAIL,
  pdf: ICON_PDF,
  ppt: ICON_POWERPOINT,
  pptx: ICON_POWERPOINT,
  pst: ICON_EMAIL,
  rtf: ICON_WORD,
  xls: ICON_EXCEL,
  xlsm: ICON_EXCEL,
  xlsx: ICON_EXCEL,
};

/**
 * Light/dark colors for the Morgan tokens, drawn from the same palette as
 * the built-in token colors so they sit naturally beside them.
 */
export const MORGAN_FILE_ICON_COLORS: Record<
  string,
  [light: string, dark: string]
> = {
  email: ["#1ca1c7", "#68cdf2"],
  excel: ["#199f43", "#5ecc71"],
  pdf: ["#6b7280", "#9ca3af"],
  powerpoint: ["#d47628", "#ffa359"],
  word: ["#1a85d4", "#69b1ff"],
};
